import { useLimitExceededEvents } from '@/hooks/useAdminAnalytics';
import { Card } from './Card';
import { DateRange } from '@/types/admin/analytics';

// Convert DateRange to days
function dateRangeToDays(dateRange: DateRange): number {
  const match = dateRange.start.match(/^-(\d+)d$/);
  if (match) return parseInt(match[1], 10);
  return 7; // default
}

interface Props {
  dateRange: DateRange;
}

/**
 * Limit Exceeded Card
 * Professional display of users approaching or exceeding usage limits
 */
export function LimitExceededCard({ dateRange }: Props) {
  const days = dateRangeToDays(dateRange);
  const { data, isLoading, error } = useLimitExceededEvents(days);

  const hasData = data && (data.count > 0 || data.near_limit_count > 0 || (data.by_feature && data.by_feature.length > 0));

  const AlertIcon = (
    <svg className="w-5 h-5 text-brand-electric-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );

  return (
    <Card
      title="Usage Limits Status"
      subtitle={`Users at or near their plan limits (${days} days)`}
      isLoading={isLoading}
      error={error?.message || null}
      icon={AlertIcon}
      accentColor="from-red-500 to-orange-500"
    >
      {!hasData ? (
        <div className="flex flex-col items-center justify-center h-48 text-gray-400">
          <svg className="w-16 h-16 mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium">All users within limits</p>
          <p className="text-xs mt-1 opacity-60">No users have exceeded their plan limits</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Main KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* At Limit */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">At Limit</p>
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <p className="text-3xl font-bold text-red-700">{data?.count || 0}</p>
              <p className="text-xs text-red-600/70 mt-1">users exceeded</p>
            </div>

            {/* Near Limit */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider">Near Limit</p>
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <p className="text-3xl font-bold text-orange-700">{data?.near_limit_count || 0}</p>
              <p className="text-xs text-orange-600/70 mt-1">users at risk (80%+)</p>
            </div>

            {/* Trend */}
            <div className={`${data?.trend_direction === 'up' ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'} border rounded-lg p-4 hover:shadow-md transition-shadow`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: data?.trend_direction === 'up' ? '#dc2626' : '#059669' }}>
                  Trend
                </p>
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: data?.trend_direction === 'up' ? '#fee2e2' : '#ecfdf5' }}>
                  <svg
                    className={`w-4 h-4 ${data?.trend_direction === 'up' ? 'text-red-600' : 'text-emerald-600'}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    {data?.trend_direction === 'up' ? (
                      <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    ) : (
                      <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    )}
                  </svg>
                </div>
              </div>
              <p className="text-3xl font-bold" style={{ color: data?.trend_direction === 'up' ? '#dc2626' : '#059669' }}>
                {Math.abs(data?.trend_percentage || 0)}%
              </p>
              <p className="text-xs mt-1" style={{ color: data?.trend_direction === 'up' ? '#991b1b' : '#065f46' }}>
                {data?.trend_direction === 'up' ? 'increasing' : 'decreasing'}
              </p>
            </div>
          </div>

          {/* By Feature Breakdown */}
          {data?.by_feature && data.by_feature.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                Breakdown by Feature
              </p>
              <div className="space-y-2">
                {data.by_feature.map((feature: any) => (
                  <div key={feature.feature_key} className="flex items-center justify-between bg-white rounded-md p-3 border border-gray-100 hover:border-gray-300 transition-colors">
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {feature.feature_key.replace(/_/g, ' ')}
                    </span>
                    <div className="flex gap-2">
                      {(feature.users_exceeded || 0) > 0 && (
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-700">
                          {feature.users_exceeded} exceeded
                        </span>
                      )}
                      {(feature.users_near_limit || 0) > 0 && (
                        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-orange-100 text-orange-700">
                          {feature.users_near_limit} near
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {data?.recent_activity && data.recent_activity.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Recent Activity ({days}d)
              </p>
              <div className="space-y-2">
                {data.recent_activity.map((activity: any) => (
                  <div key={activity.feature_key} className="flex items-center justify-between bg-white rounded-md p-3 border border-gray-100 hover:border-gray-300 transition-colors">
                    <span className="text-sm text-gray-700 capitalize">
                      {activity.feature_key.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs font-medium text-gray-900">
                      {activity.event_count} events
                      <span className="text-gray-500 ml-1">({activity.unique_users} users)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
